import {
  type HereAndNowTab,
  type UsageStatsType,
  useBuildingResourcesLoader,
  useHaNFloor,
  useHaNMobileView,
  useHereAndNowFilterData,
  useOccupancyLoader,
  useRefreshBucket,
  useRoomsTimeWindow,
  useTypologyLoader,
  useUsageStats,
} from "@lib/logic";
import { getIsOccupancyEnabled, useAppSelector } from "@lib/store";
import { useFeatureToggle } from "@hooks";
import { getDeskAmenityIcon, getRoomEquipementIcon } from "@utils";

import { refreshClock } from "../state/refresh/refreshClock";
import { haNSession } from "../state/session/haNSession";
import { useHaNBuilding } from "./useHaNBuilding";

const USAGE_STATS_TYPE_BY_TAB: Record<HereAndNowTab, UsageStatsType> = {
  rooms: "rooms",
  workspaces: "workspaces",
  explore: "none",
};

/** Everything H&N shows, for the current building and floor. Loads what's missing or stale. */
export const useHereAndNowData = (tab: HereAndNowTab) => {
  const enabledFeatures = useFeatureToggle();
  const isOccupancyEnabled = useAppSelector(getIsOccupancyEnabled);
  const bucket = useRefreshBucket(refreshClock);

  const building = useHaNBuilding(bucket);
  const floor = useHaNFloor({ session: haNSession, buildingId: building.buildingId });
  const mobileView = useHaNMobileView({ session: haNSession, buildingId: building.buildingId });

  // Nothing loads until the building is settled, so a late reservation doesn't load the default building first
  const loadingBuildingId = building.isSettled ? building.buildingId : null;

  // Each tab loads only what it shows. Explore needs both: opening a room shows its availability, opening an area
  // its occupancy.
  const showsRooms = tab !== "workspaces";
  const showsOccupancy = tab !== "rooms";

  const rooms = useRoomsTimeWindow({
    session: haNSession,
    buildingId: loadingBuildingId,
    floorId: floor.floorId,
    bucket,
    enabled: showsRooms,
  });

  useOccupancyLoader({
    buildingId: loadingBuildingId,
    enabled: showsOccupancy && isOccupancyEnabled && !!enabledFeatures.WorkspacesOnHereAndNow,
    bucket,
  });
  useTypologyLoader(loadingBuildingId);
  useBuildingResourcesLoader(loadingBuildingId);

  const buildingId = building.buildingId ?? "";

  const filterData = useHereAndNowFilterData({
    buildingId,
    floorId: floor.floorId,
    enabledFeatures,
    startDateTime: rooms.roomDateTimes.startDateTime,
    endDateTime: rooms.roomDateTimes.endDateTime,

    getDeskAmenityIcon,
    getRoomEquipementIcon,
  });

  const usageStatsPerFloor = useUsageStats({
    buildingId,
    type: USAGE_STATS_TYPE_BY_TAB[tab],
    roomRequestScope: rooms.roomDateTimes,
  });

  return { bucket, building, floor, mobileView, rooms, filterData, usageStatsPerFloor };
};

export type HereAndNowData = ReturnType<typeof useHereAndNowData>;
